/* ═══════════════════════════════════════════════════════════════
   useModuleDraft — the Module Builder's SHARED DRAFT (docs/SYNC_PLAN.md
   §2.5, D14). The same machinery a spec master's draft uses, for a module:

     · the editor is bound to a live Yjs document `module:<id>` (one
       fragment, `content`), so two people can co-edit a definition;
     · the document is seeded ONCE (by the seat the server names) from the
       server-held `draft_chunks` when one exists, else the published
       `chunks`;
     · the editor's own edits are mirrored to `draft_chunks` (1.5 s debounce,
       the `collabMirror` wire flag) so the draft outlives a closed tab;
     · Publish moves the draft to the published `chunks` SERVER-SIDE
       (`module_publish`); Discard clears `draft_chunks` (the server resets
       the document and every peer is closed with 4410).

   Only active for an EXISTING module the user may edit. A brand-new module
   has no row yet (the builder creates it first), and a viewer without
   `modules.edit` just reads the published module.
   ═══════════════════════════════════════════════════════════════ */

import React from 'react';
import { yXmlFragmentToProsemirrorJSON } from 'y-prosemirror';
import type * as Y from 'yjs';
import type { ContentBlock } from '../../shared/types';
import {
  moduleBlocksFromChunks,
  moduleChunksFromBlocks,
  validateModuleStructure,
  type ModuleChunk,
  type SpecModule,
} from '../../shared/spec-modules';
import { ModuleService } from '../services/moduleService';
import { reportError } from '../services/errorLog';
import { docToBlocks } from '../designer/schema';
import { useCollabDoc } from './useCollabDoc';
import { COLLAB_FIELD, type SpecCanvasCollab } from './collabBinding';
import { SEED_ORIGIN, fillFragment, isDocSeeded, seedDoc } from './collabSeed';
import {
  collabDocName,
  isDraftEndedError,
  mirrorWriteAllowed,
  shouldSeed,
  type CollabPeer,
  type LiveBlock,
} from './collabState';
import {
  deriveModuleChipState,
  moduleDraftDiffers,
  moduleSeedChunks,
  type ModuleChipState,
} from './moduleDraftState';

/** Debounce of the JSON mirror — the same beat as the spec master's. */
export const MODULE_MIRROR_MS = 1500;

const META_ORIGIN = 'sf-meta';
const REVERT_ORIGIN = 'sf-module-revert';

export type ModuleDraftLoad = 'idle' | 'loading' | 'ready' | 'error';

export interface UseModuleDraftOptions {
  /** The existing module's id; null ⇒ nothing to share yet (new module). */
  moduleId: string | null;
  /** The user holds `modules.edit`. Without it the draft is not opened. */
  enabled: boolean;
  userName: string;
  toast: (msg: string, isError?: boolean) => void;
  /** The server's current row, handed up on open and after every reopen. */
  onBaseline: (module: SpecModule) => void;
  /** Starting blocks for a definition with no content at all. */
  blankBlocks: () => ContentBlock[];
}

export interface ModuleDraftApi {
  /** A shared draft is in play for this module. */
  active: boolean;
  loadState: ModuleDraftLoad;
  /** Blocks to show until the shared document is bound. */
  initialBlocks: ContentBlock[];
  /** Present once the document has synced AND been seeded. */
  collab: SpecCanvasCollab | null;
  chip: ModuleChipState;
  /** A draft exists that differs from the published module. */
  pending: boolean;
  peers: CollabPeer[];
  liveBlocked: LiveBlock | null;
  /** The live document's blocks (valid once `collab` is bound). */
  readBlocks: () => ContentBlock[];
  /** The PUBLISHED module's blocks (what Publish is walked against). */
  readPublished: () => ContentBlock[];
  /** Write the mirror now (Publish needs the server copy current). */
  flush: () => Promise<boolean>;
  /** Discard the draft for everyone. */
  discard: () => Promise<boolean>;
  /** Flush, publish server-side, reopen on the new definition. */
  publish: (message?: string) => Promise<{ version: number | null; raised: number }>;
  /** Load a past version's chunks into the draft (counts as an edit). */
  loadChunks: (chunks: ModuleChunk[]) => void;
}

export function useModuleDraft(opts: UseModuleDraftOptions): ModuleDraftApi {
  const { moduleId, enabled, userName } = opts;
  const active = !!moduleId && enabled;

  const [loadState, setLoadState] = React.useState<ModuleDraftLoad>('idle');
  const [initialBlocks, setInitialBlocks] = React.useState<ContentBlock[]>([]);
  const [edited, setEdited] = React.useState(false);
  const [seeded, setSeeded] = React.useState(false);
  const [seeder, setSeeder] = React.useState<string | null>(null);
  const [mirrorPending, setMirrorPending] = React.useState(false);
  const [epoch, setEpoch] = React.useState(0);

  const optsRef = React.useRef(opts);
  optsRef.current = opts;
  const moduleRef = React.useRef<SpecModule | null>(null);
  const draftChunksRef = React.useRef<ModuleChunk[] | null>(null);
  const editedRef = React.useRef(false);
  editedRef.current = edited;
  const seededRef = React.useRef(false);
  seededRef.current = seeded;
  /** The draft ENDED for this connection (our own publish / discard, a
   *  teammate's, or a 409 draft-ended): no mirror write may recreate it. */
  const endedRef = React.useRef(false);
  /** The Y.Doc this window ended itself — its close is not news. */
  const selfEndedDocRef = React.useRef<Y.Doc | null>(null);

  // ─── The shared document ───
  const docName =
    active && loadState === 'ready' && moduleId ? collabDocName('module', moduleId) : null;
  const collabDoc = useCollabDoc(docName, userName, { announce: true, epoch });
  const ydoc = collabDoc?.ydoc ?? null;
  const provider = collabDoc?.provider ?? null;
  const ydocRef = React.useRef<Y.Doc | null>(null);
  ydocRef.current = ydoc;

  /** The document as it stands, in the module's own chunk shape. */
  const currentBlocks = React.useCallback((): ContentBlock[] => {
    const d = ydocRef.current;
    if (!d) return [];
    return docToBlocks(yXmlFragmentToProsemirrorJSON(d.getXmlFragment(COLLAB_FIELD)));
  }, []);

  const readPublished = React.useCallback(
    (): ContentBlock[] => moduleBlocksFromChunks(moduleRef.current?.chunks ?? []),
    []
  );

  // ─── Load (always the server's CURRENT row — never the cache) ───
  const load = React.useCallback(async () => {
    if (!moduleId) return;
    setLoadState('loading');
    try {
      const m = await ModuleService.getFresh(moduleId);
      if (!m) {
        setLoadState('error');
        return;
      }
      moduleRef.current = m;
      draftChunksRef.current = m.draftChunks ?? null;
      const blocks = moduleBlocksFromChunks(moduleSeedChunks(m.chunks, m.draftChunks));
      setInitialBlocks(blocks.length ? blocks : optsRef.current.blankBlocks());
      endedRef.current = false;
      editedRef.current = !!m.draftChunks;
      setEdited(!!m.draftChunks);
      seededRef.current = false;
      setSeeded(false);
      setSeeder(null);
      setMirrorPending(false);
      optsRef.current.onBaseline(m);
      setLoadState('ready');
    } catch (err) {
      reportError('ModuleBuilder:draft-load', err);
      setLoadState('error');
    }
  }, [moduleId]);

  React.useEffect(() => {
    if (!active) {
      setLoadState('idle');
      return;
    }
    void load();
  }, [active, load]);

  const reopenRef = React.useRef<Promise<void> | null>(null);
  /** Re-read the module and open a fresh document (after a publish, a
   *  discard, or a teammate doing either). */
  const reopen = React.useCallback((): Promise<void> => {
    if (reopenRef.current) return reopenRef.current;
    const p = (async () => {
      try {
        await load();
        setEpoch((e) => e + 1);
      } finally {
        reopenRef.current = null;
      }
    })();
    reopenRef.current = p;
    return p;
  }, [load]);

  // ─── JSON mirror → draft_chunks ───
  const mirrorTimer = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const mirrorInFlight = React.useRef<Promise<boolean> | null>(null);
  const mirrorAgain = React.useRef(false);

  const writeMirror = React.useCallback(
    async (force: boolean): Promise<boolean> => {
      if (mirrorInFlight.current) {
        mirrorAgain.current = true;
        return mirrorInFlight.current;
      }
      const run = async (): Promise<boolean> => {
        const id = optsRef.current.moduleId;
        if (!id || !seededRef.current) return false;
        if (!mirrorWriteAllowed({ ended: endedRef.current, docDiscarded: false })) return false;
        if (!force && !editedRef.current) return false;
        const blocks = currentBlocks();
        // A structurally broken document would write a lossy definition (orphans
        // are dropped); the live document keeps it, the mirror waits for a fix.
        if (validateModuleStructure(blocks).length) return false;
        try {
          await ModuleService.writeDraftMirror(id, moduleChunksFromBlocks(blocks));
        } catch (err) {
          if (isDraftEndedError(err)) {
            endedRef.current = true;
            void reopen();
            return false;
          }
          throw err;
        }
        return true;
      };
      const p = (async () => {
        try {
          let ok = await run();
          while (mirrorAgain.current) {
            mirrorAgain.current = false;
            ok = await run();
          }
          return ok;
        } finally {
          mirrorInFlight.current = null;
        }
      })();
      mirrorInFlight.current = p;
      return p;
    },
    [currentBlocks, reopen]
  );

  const cancelMirror = React.useCallback(() => {
    if (mirrorTimer.current) {
      clearTimeout(mirrorTimer.current);
      mirrorTimer.current = null;
    }
    mirrorAgain.current = false;
    setMirrorPending(false);
  }, []);

  const flush = React.useCallback(
    async (force = false): Promise<boolean> => {
      if (mirrorTimer.current) {
        clearTimeout(mirrorTimer.current);
        mirrorTimer.current = null;
      }
      try {
        await writeMirror(force);
        return true;
      } catch (err) {
        reportError('ModuleBuilder:mirror', err);
        optsRef.current.toast(
          err instanceof Error && err.message ? err.message : 'Could not save the draft',
          true
        );
        return false;
      } finally {
        setMirrorPending(false);
      }
    },
    [writeMirror]
  );

  const scheduleMirror = React.useCallback(() => {
    if (!mirrorWriteAllowed({ ended: endedRef.current, docDiscarded: false })) return;
    setMirrorPending(true);
    if (mirrorTimer.current) clearTimeout(mirrorTimer.current);
    mirrorTimer.current = setTimeout(() => {
      mirrorTimer.current = null;
      void flush();
    }, MODULE_MIRROR_MS);
  }, [flush]);

  const markEdited = React.useCallback(() => {
    const d = ydocRef.current;
    if (!d || editedRef.current) return;
    editedRef.current = true;
    setEdited(true);
    d.transact(() => d.getMap('meta').set('edited', true), META_ORIGIN);
  }, []);

  // ─── Observe the shared document ───
  React.useEffect(() => {
    if (!ydoc || !provider) return;
    const fragment = ydoc.getXmlFragment(COLLAB_FIELD);
    const meta = ydoc.getMap<unknown>('meta');

    const readMeta = () => {
      if (meta.get('edited') === true) {
        editedRef.current = true;
        setEdited(true);
      }
      const s = meta.get('seeder');
      setSeeder(typeof s === 'string' ? s : null);
      if (meta.get('seeded') === true || fragment.length > 0) setSeeded(true);
    };
    const onUpdate = (_update: Uint8Array, origin: unknown) => {
      // Remote (the provider applied it), seeding, or our own meta flag —
      // none of those is this user editing.
      if (origin === provider || origin === SEED_ORIGIN || origin === META_ORIGIN) return;
      if (!seededRef.current) return;
      if (!editedRef.current) {
        const chunks = moduleChunksFromBlocks(currentBlocks());
        if (!moduleDraftDiffers(chunks, moduleRef.current?.chunks)) return;
        markEdited();
      }
      scheduleMirror();
    };

    meta.observe(readMeta);
    ydoc.on('update', onUpdate);
    readMeta();
    return () => {
      meta.unobserve(readMeta);
      ydoc.off('update', onUpdate);
    };
  }, [ydoc, provider, scheduleMirror, markEdited, currentBlocks]);

  // ─── Seed (exactly one writer, once) ───
  const synced = !!collabDoc?.synced;
  const seat = collabDoc?.seat ?? '';
  React.useEffect(() => {
    if (!ydoc || !synced) return;
    if (isDocSeeded(ydoc)) {
      if (!seededRef.current) {
        seededRef.current = true;
        setSeeded(true);
      }
      return;
    }
    if (
      !shouldSeed({
        synced,
        readOnly: !enabled,
        fragmentEmpty: ydoc.getXmlFragment(COLLAB_FIELD).length === 0,
        seeded: false,
        seeder,
        mySeat: seat,
      })
    ) {
      return;
    }
    const m = moduleRef.current;
    if (!m) return;
    const rows = moduleBlocksFromChunks(moduleSeedChunks(m.chunks, draftChunksRef.current));
    const blocks = rows.length ? rows : optsRef.current.blankBlocks();
    // ONE transaction: content and the flags land together.
    seedDoc(ydoc, blocks, () => {
      if (draftChunksRef.current) ydoc.getMap<unknown>('meta').set('edited', true);
    });
    seededRef.current = true;
    setSeeded(true);
  }, [ydoc, synced, seeder, seat, enabled]);

  // ─── The draft ended under us (4409 / 4410): re-read and reopen ───
  const docDiscarded = !!collabDoc?.discarded;
  const docReset = !!collabDoc?.reset;
  const endedDoc = collabDoc?.ydoc ?? null;
  const handledDocRef = React.useRef<Y.Doc | null>(null);
  React.useEffect(() => {
    if (!docDiscarded || !endedDoc || handledDocRef.current === endedDoc) return;
    handledDocRef.current = endedDoc;
    endedRef.current = true;
    cancelMirror();
    if (selfEndedDocRef.current !== endedDoc) {
      optsRef.current.toast(
        docReset
          ? 'This draft was refreshed — reloading it'
          : 'This draft was closed by a teammate — published or discarded'
      );
    }
    void reopen();
  }, [docDiscarded, docReset, endedDoc, cancelMirror, reopen]);

  // Leaving mid-burst must not lose the burst.
  React.useEffect(
    () => () => {
      if (mirrorTimer.current && !endedRef.current) void flush();
    },
    [flush]
  );

  // ─── Actions ───
  const discard = React.useCallback(async (): Promise<boolean> => {
    const id = optsRef.current.moduleId;
    if (!id) return false;
    selfEndedDocRef.current = ydocRef.current;
    endedRef.current = true;
    cancelMirror();
    try {
      await ModuleService.discardDraft(id);
      await reopen();
      return true;
    } catch (err) {
      reportError('ModuleBuilder:discard', err);
      endedRef.current = false;
      selfEndedDocRef.current = null;
      optsRef.current.toast(
        'Discard failed: ' + (err instanceof Error ? err.message : 'Unknown error'),
        true
      );
      return false;
    }
  }, [cancelMirror, reopen]);

  const publish = React.useCallback(
    async (message?: string) => {
      const id = optsRef.current.moduleId;
      if (!id) throw new Error('This module has not been saved yet');
      // The server publishes ITS copy of the draft, so it must be current.
      if (!(await flush(true)))
        throw new Error('The draft could not be saved, so it was not published');
      selfEndedDocRef.current = ydocRef.current;
      endedRef.current = true;
      cancelMirror();
      try {
        const res = await ModuleService.publish(id, message);
        await reopen();
        return res;
      } catch (err) {
        endedRef.current = false;
        selfEndedDocRef.current = null;
        throw err;
      }
    },
    [flush, cancelMirror, reopen]
  );

  const loadChunks = React.useCallback(
    (chunks: ModuleChunk[]) => {
      const d = ydocRef.current;
      if (!d || !seededRef.current) return;
      const rows = moduleBlocksFromChunks(chunks);
      const blocks = rows.length ? rows : optsRef.current.blankBlocks();
      // Not a seed origin: a revert is a real edit and must mirror.
      d.transact(() => fillFragment(d.getXmlFragment(COLLAB_FIELD), blocks), REVERT_ORIGIN);
      markEdited();
      scheduleMirror();
    },
    [markEdited, scheduleMirror]
  );

  // Bind the canvas once the document has synced AND been seeded — and KEEP
  // it bound through a later disconnect (offline typing queues in the Y.Doc).
  const [boundDoc, setBoundDoc] = React.useState<Y.Doc | null>(null);
  React.useEffect(() => {
    if (ydoc && seeded && synced) setBoundDoc(ydoc);
  }, [ydoc, seeded, synced]);
  const boundUser = collabDoc?.user ?? null;
  const collab = React.useMemo<SpecCanvasCollab | null>(
    () =>
      ydoc && provider && boundUser && boundDoc === ydoc && !docDiscarded
        ? { ydoc, provider, user: boundUser }
        : null,
    [ydoc, provider, boundUser, boundDoc, docDiscarded]
  );

  const chip = deriveModuleChipState({
    status: collabDoc?.status ?? 'connecting',
    unsyncedChanges: collabDoc?.unsyncedChanges ?? 0,
    mirrorPending,
    ready: seeded && synced,
    pending: edited,
  });

  return {
    active,
    loadState,
    initialBlocks,
    collab,
    chip,
    pending: edited,
    peers: collabDoc?.peers ?? [],
    liveBlocked: collabDoc?.blocked ?? null,
    readBlocks: currentBlocks,
    readPublished,
    flush,
    discard,
    publish,
    loadChunks,
  };
}
